import {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { SupportTicketThreadContext } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { BasicIndex } from "@tanstack/db";
import {
  createCollection,
  localOnlyCollectionOptions,
} from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Schema } from "effect";
import { getSupportTicketIdScope } from "./support-ticket-collection-scope";
import { supportTicketQueryCollectionOptions } from "./support-ticket-query-collection-options";
import {
  supportTicketQueryScopeKey,
  type SupportTicketQueryScope,
} from "./support-ticket-query-keys";
import { parseCollectionFilters } from "./support-ticket-tanstack-scope";
import {
  fetchSupportTicketMessages,
  fetchSupportTicketThreads,
  type SupportTicketMessageLink,
  type SupportTicketThreadLink,
  type SupportTicketTranscriptApi,
} from "./support-ticket-transcript-requests";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** Entity rows are raw ChannelThread/Message; per-ticket links live below. */
const SupportTicketThreadItemSchema = ChannelThread;

const SupportTicketMessageItemSchema = Message;

export const SupportTicketThreadLinkSchema = Schema.Struct({
  /** Composite key: `${supportTicketId}:${threadId}` */
  id: Schema.String,
  supportTicketId: SupportTicketId,
  threadId: ChannelThreadId,
  ticketContext: Schema.optional(SupportTicketThreadContext),
  messageCount: Schema.optional(Schema.Number),
});
export type SupportTicketThreadLinkRecord =
  typeof SupportTicketThreadLinkSchema.Type;

export const SupportTicketMessageLinkSchema = Schema.Struct({
  /** Composite key: `${supportTicketId}:${messageId}` */
  id: Schema.String,
  supportTicketId: SupportTicketId,
  messageId: MessageId,
});
export type SupportTicketMessageLinkRecord =
  typeof SupportTicketMessageLinkSchema.Type;

export function threadLinkKey(
  supportTicketId: SupportTicketId,
  threadId: ChannelThreadId,
): string {
  return `${String(supportTicketId)}:${String(threadId)}`;
}

export function messageLinkKey(
  supportTicketId: SupportTicketId,
  messageId: MessageId,
): string {
  return `${String(supportTicketId)}:${String(messageId)}`;
}

export const supportTicketTranscriptQueryKeys = {
  all: ["supportTickets", "transcript"] as const,
  threads: () => ["supportTickets", "transcript", "threads"] as const,
  threadsCollection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketTranscriptQueryKeys.threads(),
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  messages: () => ["supportTickets", "transcript", "messages"] as const,
  messagesCollection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketTranscriptQueryKeys.messages(),
      ...supportTicketQueryScopeKey(scope),
    ] as const,
  timelineEvents: () =>
    ["supportTickets", "transcript", "timelineEvents"] as const,
  timelineEventsCollection: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketTranscriptQueryKeys.timelineEvents(),
      ...supportTicketQueryScopeKey(scope),
    ] as const,
};

export interface CreateSupportTicketTranscriptCollectionsOptions {
  queryClient: QueryClient;
  api: SupportTicketTranscriptApi;
  scope?: SupportTicketQueryScope;
}

function getSupportTicketIdFromLoadSubsetOptions(
  loadSubsetOptions: unknown,
): SupportTicketId | null {
  return getSupportTicketIdScope(parseCollectionFilters(loadSubsetOptions));
}

export type ThreadLinksCollection = ReturnType<
  typeof createThreadLinksCollection
>;
export type MessageLinksCollection = ReturnType<
  typeof createMessageLinksCollection
>;

/** Local-only; populated as side-effect of entity fetches + SSE + reconcile. */
export function createThreadLinksCollection() {
  return createCollection(
    localOnlyCollectionOptions({
      getKey: (item) => item.id,
      schema: Schema.standardSchemaV1(
        Schema.typeSchema(SupportTicketThreadLinkSchema),
      ),
    }),
  );
}

export function createMessageLinksCollection() {
  return createCollection(
    localOnlyCollectionOptions({
      getKey: (item) => item.id,
      schema: Schema.standardSchemaV1(
        Schema.typeSchema(SupportTicketMessageLinkSchema),
      ),
    }),
  );
}

export function writeThreadLinks(
  linksCollection: ThreadLinksCollection,
  supportTicketId: SupportTicketId,
  links: readonly SupportTicketThreadLink[],
): void {
  if (links.length === 0) {
    return;
  }
  /** Dedupe: repeat insert() on the same key throws DuplicateKeyError. */
  const seen = new Set<string>();
  for (const link of links) {
    const id = threadLinkKey(supportTicketId, link.threadId);
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    const record: SupportTicketThreadLinkRecord = {
      id,
      supportTicketId,
      threadId: link.threadId,
      ...(link.ticketContext !== undefined
        ? { ticketContext: link.ticketContext }
        : {}),
      ...(link.messageCount !== undefined
        ? { messageCount: link.messageCount }
        : {}),
    };
    if (linksCollection.has(id)) {
      linksCollection.update(id, (draft) => {
        draft.supportTicketId = record.supportTicketId;
        draft.threadId = record.threadId;
        draft.ticketContext = record.ticketContext;
        draft.messageCount = record.messageCount;
      });
    } else {
      linksCollection.insert(record);
    }
  }
}

export function writeMessageLinks(
  linksCollection: MessageLinksCollection,
  supportTicketId: SupportTicketId,
  links: readonly SupportTicketMessageLink[],
): void {
  if (links.length === 0) {
    return;
  }
  for (const link of links) {
    const id = messageLinkKey(supportTicketId, link.messageId);
    const record: SupportTicketMessageLinkRecord = {
      id,
      supportTicketId,
      messageId: link.messageId,
    };
    if (linksCollection.has(id)) {
      linksCollection.update(id, (draft) => {
        draft.supportTicketId = record.supportTicketId;
        draft.messageId = record.messageId;
      });
    } else {
      linksCollection.insert(record);
    }
  }
}

/** Replace this ticket's link rows; other tickets' rows are untouched. */
export function replaceThreadLinksForSupportTicket(
  linksCollection: ThreadLinksCollection,
  supportTicketId: SupportTicketId,
  links: readonly SupportTicketThreadLink[],
): void {
  const keep = new Set(
    links.map((link) => threadLinkKey(supportTicketId, link.threadId)),
  );
  const supportTicketKey = String(supportTicketId);
  for (const existing of linksCollection.values()) {
    if (
      String(existing.supportTicketId) === supportTicketKey &&
      !keep.has(existing.id)
    ) {
      linksCollection.delete(existing.id);
    }
  }
  writeThreadLinks(linksCollection, supportTicketId, links);
}

export function replaceMessageLinksForSupportTicket(
  linksCollection: MessageLinksCollection,
  supportTicketId: SupportTicketId,
  links: readonly SupportTicketMessageLink[],
): void {
  const keep = new Set(
    links.map((link) => messageLinkKey(supportTicketId, link.messageId)),
  );
  const supportTicketKey = String(supportTicketId);
  for (const existing of linksCollection.values()) {
    if (
      String(existing.supportTicketId) === supportTicketKey &&
      !keep.has(existing.id)
    ) {
      linksCollection.delete(existing.id);
    }
  }
  writeMessageLinks(linksCollection, supportTicketId, links);
}

export interface CreateThreadsCollectionOptions extends CreateSupportTicketTranscriptCollectionsOptions {
  threadLinks: ThreadLinksCollection;
}

export function createThreadsCollection({
  queryClient,
  api,
  scope,
  threadLinks,
}: CreateThreadsCollectionOptions) {
  return createCollection(
    supportTicketQueryCollectionOptions({
      queryKey: supportTicketTranscriptQueryKeys.threadsCollection(scope),
      queryFn: async (ctx): Promise<ChannelThread[]> => {
        const supportTicketId = getSupportTicketIdFromLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        if (!supportTicketId) {
          return [];
        }
        const { entities, links } = await fetchSupportTicketThreads(
          api,
          supportTicketId,
        );
        writeThreadLinks(threadLinks, supportTicketId, links);
        return [...entities];
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      schema: Schema.standardSchemaV1(
        Schema.typeSchema(SupportTicketThreadItemSchema),
      ),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
      recoverTransientFailures: false,
    }),
  );
}

export interface CreateMessagesCollectionOptions extends CreateSupportTicketTranscriptCollectionsOptions {
  messageLinks: MessageLinksCollection;
}

export function createMessagesCollection({
  queryClient,
  api,
  scope,
  messageLinks,
}: CreateMessagesCollectionOptions) {
  return createCollection(
    supportTicketQueryCollectionOptions({
      queryKey: supportTicketTranscriptQueryKeys.messagesCollection(scope),
      queryFn: async (ctx): Promise<Message[]> => {
        const supportTicketId = getSupportTicketIdFromLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        if (!supportTicketId) {
          return [];
        }
        const { entities, links } = await fetchSupportTicketMessages(
          api,
          supportTicketId,
        );
        writeMessageLinks(messageLinks, supportTicketId, links);
        return [...entities];
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      schema: Schema.standardSchemaV1(
        Schema.typeSchema(SupportTicketMessageItemSchema),
      ),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
      recoverTransientFailures: false,
    }),
  );
}

export function createTimelineEventsCollection({
  queryClient,
  api,
  scope,
}: CreateSupportTicketTranscriptCollectionsOptions) {
  return createCollection(
    supportTicketQueryCollectionOptions({
      queryKey:
        supportTicketTranscriptQueryKeys.timelineEventsCollection(scope),
      queryFn: async (ctx): Promise<SupportTicketTimelineEventResponse[]> => {
        const supportTicketId = getSupportTicketIdFromLoadSubsetOptions(
          ctx.meta?.loadSubsetOptions,
        );
        if (!supportTicketId) {
          return [];
        }

        const result = await api.getTimelineEvents(supportTicketId);
        return [...result.items];
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      schema: Schema.standardSchemaV1(
        Schema.typeSchema(SupportTicketTimelineEventResponse),
      ),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
      recoverTransientFailures: false,
    }),
  );
}

export type ThreadsCollection = ReturnType<typeof createThreadsCollection>;
export type MessagesCollection = ReturnType<typeof createMessagesCollection>;
export type TimelineEventsCollection = ReturnType<
  typeof createTimelineEventsCollection
>;

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  fetchSupportTicketMessages,
  fetchSupportTicketThreads,
  type SupportTicketMessageLink,
  type SupportTicketMessagesFetchResult,
  type SupportTicketThreadCollectionItem,
  type SupportTicketThreadLink,
  type SupportTicketThreadsFetchResult,
  type SupportTicketTranscriptApi,
} from "./support-ticket-transcript-requests";
